import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

type TranslationKey = 'en' | 'he';

interface LanguageContextType {
  currentLang: TranslationKey;
  setLanguage: (lang: TranslationKey) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const [currentLang, setCurrentLang] = useState<TranslationKey>('he');

  useEffect(() => {
    // Initialize language from i18n
    setCurrentLang(i18n.language as TranslationKey);
  }, [i18n.language]);

  const handleSetLanguage = (lang: TranslationKey) => {
    setCurrentLang(lang);
    i18n.changeLanguage(lang);
  };

  return (
    <LanguageContext.Provider
      value={{
        currentLang,
        setLanguage: handleSetLanguage,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}; 